import {
    HttpTransportType,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from "@microsoft/signalr";
import lodash from "lodash";

export default class Streaming {
    constructor() {
        this.streamObj = { Platform: "hisselab", SymbolObjectIds: [] };
        this.historyArgs = {
            Platform: "hisselab",
            Symbol: "",
            StartDate: "",
            EndDate: "",
            Resolution: "",
            FieldType: "",
        };
        this.infoObj = { Platform: "hisselab", SymbolObjectId: "" };
        this.callbackFunctions = { onHistoryCallback: null, onInfoCallback: null };
        this.observers = [];
        this.isInvoked = false;
        this.debouncedStreamInvoke = lodash.debounce(
            () => this.streamInvoke(),
            500,
            { leading: true }
        );
        this.buildConnect();
    }

    buildConnect() {
        this.hubConnection = new HubConnectionBuilder()
            .configureLogging(LogLevel.Information)
            .withUrl("https://streaming.data.hub.equityrt.com/data.hub", {
                withCredentials: false,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect([0, 3000, 5000, 10000, 15000, 30000])
            .build();
        this.registerHandlers();
        this.startConnection().catch(err =>
            console.error("Connection start failed:", err)
        );
    }

    registerHandlers() {
        this.hubConnection.on("Info", data => {
            try {
                const parsed = JSON.parse(JSON.parse(data));
                if (this.callbackFunctions.onInfoCallback) {
                    this.callbackFunctions.onInfoCallback(parsed.info);
                }
            } catch (err) {
                console.error("Error in Info handler:", err);
            }
        });

        this.hubConnection.on("History", data => {
            try {
                const parsed = JSON.parse(data);
                const bars = [];
                const meta = { noData: true };
                for (const s of parsed.series || []) {
                    bars.push({
                        time: s.t * 1000,
                        open: s.o,
                        high: s.h,
                        low: s.l,
                        close: s.c,
                        volume: s.v,
                    });
                    meta.noData = false;
                }
                if (meta.noData && parsed.range) {
                    if (this.historyArgs.EndDate >= parsed.range.start) {
                        meta.nextTime = parsed.range.end;
                    }
                }
                if (this.callbackFunctions.onHistoryCallback) {
                    this.callbackFunctions.onHistoryCallback(bars, meta);
                }
            } catch (err) {
                console.error("Error in History handler:", err);
            }
        });

        this.hubConnection.on("Stream", data => {
            try {
                const msg = JSON.parse(data);
                const id = msg["@"];
                this.observers.forEach(obs => {
                    if (obs.objectIds.includes(id)) obs.onMessage(msg);
                });
            } catch (err) {
                console.error("Error in Stream handler:", err);
            }
        });

        this.hubConnection.onreconnecting(err =>
            console.warn("Reconnecting...", err)
        );
        this.hubConnection.onreconnected(() => {
            console.log("Reconnected, resubscribing...");
            this.isInvoked = false;
            this.invoker();
        });
        this.hubConnection.onclose(err =>
            console.warn("Connection closed:", err)
        );
    }

    startConnection() {
        if (this.hubConnection.state === HubConnectionState.Disconnected) {
            return this.hubConnection.start().then(() => {
                console.log("Hub connection started");
            });
        }
        return Promise.resolve();
    }

    invokeWithRetry(method, payload, retries = 3, backoff = 1000) {
        const attempt = n => {
            return this.hubConnection
                .invoke(method, JSON.stringify(payload))
                .catch(err => {
                    if (n < retries) {
                        console.warn(`${method} failed, retry ${n}`, err);
                        return new Promise(res => setTimeout(res, backoff * n))
                            .then(() => attempt(n + 1));
                    }
                    throw err;
                });
        };
        return attempt(1);
    }

    subscribe(observer, onMessage) {
        observer.onMessage = onMessage;
        this.observers.push(observer);
        this.isInvoked = false;
        this.debouncedStreamInvoke();
        return new Unsubscriber(this.observers, observer, this);
    }

    streamInvoke(isForceClose = false) {
        const objectIds = this.observers.reduce((acc, obs) =>
            lodash.union(acc, obs.objectIds), []);
        this.streamObj.SymbolObjectIds = objectIds;
        if (lodash.isEmpty(objectIds)) {
            this.hubConnection.stop().catch(err =>
                console.warn("Stop failed:", err)
            );
            console.log("No objectIds -> stopped streaming");
            return;
        }
        if (isForceClose) { this.resetStream(); return; }
        if (this.hubConnection.state === HubConnectionState.Connected) {
            this.invoker();
        } else if (this.hubConnection.state === HubConnectionState.Disconnected) {
            this.startConnection()
                .then(() => this.invoker())
                .catch(err => console.error("Restart failed:", err));
        } else {
            setTimeout(() => this.streamInvoke(), 1000);
        }
    }

    invoker() {
        if (this.isInvoked) return;
        this.isInvoked = true;
        this.invokeWithRetry("Stream", this.streamObj)
            .catch(err => console.error("Final Stream error:", err));
    }

    resetStream() {
        this.hubConnection
            .stop()
            .then(() => this.hubConnection.start())
            .then(() => {
                console.log("Reset and restarted");
                this.isInvoked = false;
                this.invoker();
            })
            .catch(err => console.error("ResetStream error:", err));
    }

    symbolInfo(symbol, onInfoCallback, onErrorCallback) {
        this.callbackFunctions.onInfoCallback = onInfoCallback;
        if (!symbol) return;
        this.infoObj.SymbolObjectId = symbol;
        const call = () =>
            this.invokeWithRetry("Info", this.infoObj).catch(onErrorCallback);
        if (this.hubConnection.state !== HubConnectionState.Connected) {
            setTimeout(() =>
                this.startConnection().then(call).catch(onErrorCallback), 1000
            );
        } else { call(); }
    }

    history(args, onHistoryCallback, onErrorCallback) {
        this.callbackFunctions.onHistoryCallback = onHistoryCallback;
        if (!args.Symbol) return;
        this.historyArgs = Object.assign({}, args, { Platform: "hisselab" });
        const call = () =>
            this.invokeWithRetry("History", this.historyArgs).catch(onErrorCallback);
        if (this.hubConnection.state !== HubConnectionState.Connected) {
            setTimeout(() =>
                this.startConnection().then(call).catch(onErrorCallback), 1000
            );
        } else { call(); }
    }
}

export class Unsubscriber {
    constructor(observers, observer, streaming) {
        this._observers = observers;
        this._observer = observer;
        this._streaming = streaming;
    }

    Dispose() {
        const idx = this._observers.indexOf(this._observer);
        if (idx !== -1) {
            this._observers.splice(idx, 1);
            this._streaming.streamInvoke(true);
            this._streaming.isInvoked = false;
        }
    }
}